/**
 * @deprecated
 */

import { RangeHorizon, ResampleIntervals } from '@toggle/chart';
import { AssetSubClassCode, Entity } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { InsightsGroup } from '~/components/insights-group/InsightsGroup';
import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useChartNavigate } from '~/hooks/use-chart-navigate/useChartNavigate';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { About } from '~/modules/about/About';
import { OverviewEventFeed } from '~/modules/overview-event-feed/OverviewEventFeed';
import { OverviewFinancials } from '~/modules/overview-financials/OverviewFinancials';
import { ThemeKnowledgeGraph } from '~/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { OverviewValuation } from '~/modules/overview-valuation-metrics/OverviewValuationMetrics';
import { SimpleChart } from '~/modules/simple-chart/SimpleChart';
import { useExplore } from '~/react-query-hooks/explore/useExplore';
import { useQueryInsights } from '~/react-query-hooks/insights/useInsight';
import { useCompanyTheme } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { ChartSearchParamsAssetValue } from '~/utils/chart-search-utils/chart-search-utils';
import { ChartState } from '~/views/turbo-chart/hooks/use-chart-state/useChartState';
import { ChartSettings } from '~/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { BrandsAndProductsSection } from '~/widgets/knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { CompetitorsSection } from '~/widgets/knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/CompetitorsSection';
import { GeographicExposureSection } from '~/widgets/knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/GeographicExposureSection';
import { ThemeDrawer } from '~/widgets/theme-widget/theme-drawer/ThemeDrawer';

import { UpgradeToUnlock } from './components/upgrade-to-unlock/UpgradeToUnlock';
import * as S from './Overview.styles';

export interface OverviewProps {
  entity: Entity;
}

/**
 * @deprecated
 */
export const Overview = ({ entity }: OverviewProps) => {
  const { t } = useTranslation([
    'widget',
    'common',
    'chart',
    'analyze',
    'countries',
    'scenarioCommander',
    'screener',
    'scenario',
    'whatsNew',
    'earnings',
    'calendar',
  ]);
  const { params } = useQueryParams();
  const { openDrawer } = useInsightDrawer();
  const chartNavigate = useChartNavigate();

  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const isBrandsAndProductsEnabled = useFlags(state =>
    state.getFeatureFlagState('kg_brands_products')
  );
  const { error: companyThemeError } = useCompanyTheme({
    entityTag: entity.tag,
  });
  const {
    data,
    isError: isInsightsError,
    isLoading: isInsightsLoading,
  } = useExplore({
    entityTags: [entity.tag],
    filters: {
      entity_tag: {
        values: [
          {
            tags: [entity.tag],
          },
        ],
      },
      period: { values: [{ duration: 1, unit: 'week' }] },
    },
    sortBy: undefined,
    combined: true,
  });

  const filteredInsights = useMemo(() => {
    return data?.pages[0]?.entities[0]?.insights ?? [];
  }, [data]);

  const insights = useQueryInsights(filteredInsights.map(i => i.id));

  const insightsWithMetadata = useMemo(() => {
    return filteredInsights.map(insight => {
      const metadata = insights.data.find(
        data => data.id === insight.id
      )?.metadata;

      return {
        ...insight,
        metadata,
      };
    });
  }, [insights.data, filteredInsights]);

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneHour,
    horizon: RangeHorizon.OneMonth,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;

  const navigateToChart = (chartState: ChartState) => {
    const entityValue: ChartSearchParamsAssetValue = [
      entity.tag,
      entity.default_snake,
      0,
    ];

    chartNavigate([entityValue], {
      resample: chartState.resample,
      horizon: chartState.horizon,
      seriesType: chartState.chartPanes[0].seriesType,
    });
  };

  const displayConnections = companyThemeError?.status !== 404 && isStock;
  return (
    <>
      <ThemeDrawer entity={entity} />
      <S.OverviewRoot>
        <SimpleChart
          entity={entity}
          key={entity.tag}
          initialChartSettings={chartSettings}
          isLegacyVariant={false}
          onClickFullscreen={navigateToChart}
          maxHorizon={RangeHorizon.TenYears}
          eventsConfig={{
            TMI: true,
          }}
        />
        <S.Flex>
          <S.EventFeedWrapper>
            {isBasicOrLightUser ? (
              <S.UpgradeToUnlockWrapper>
                <UpgradeToUnlock />
              </S.UpgradeToUnlockWrapper>
            ) : (
              <ErrorBoundaryWrapper>
                <OverviewEventFeed entityTag={entity.tag} />
              </ErrorBoundaryWrapper>
            )}
          </S.EventFeedWrapper>
          <S.SplitSection>
            <S.StyledAboutWrapper>
              <About entity={entity} variant="secondary" />
            </S.StyledAboutWrapper>
          </S.SplitSection>
        </S.Flex>
        <S.Flex>
          {displayConnections && (
            <S.SplitSection>
              <SectionTitle
                title={t('widget:assetOverview.knowledgeGraph.name')}
              />
              <S.KnowledgeGraphWrapper>
                <ErrorBoundaryWrapper>
                  <ThemeKnowledgeGraph
                    entityTag={entity.tag}
                    isInDrawer={false}
                  />
                </ErrorBoundaryWrapper>
                {isBrandsAndProductsEnabled && (
                  <ErrorBoundaryWrapper>
                    <BrandsAndProductsSection entityTag={entity.tag} />
                  </ErrorBoundaryWrapper>
                )}
                <ErrorBoundaryWrapper>
                  <CompetitorsSection entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
                <ErrorBoundaryWrapper>
                  <GeographicExposureSection entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </S.KnowledgeGraphWrapper>
            </S.SplitSection>
          )}
        </S.Flex>
        {isStock && (
          <>
            <OverviewFinancials entity={entity} />
            <OverviewValuation entity={entity} />
          </>
        )}

        {!!insightsWithMetadata.length && (
          <S.Flex>
            <S.SplitSection>
              <InsightsGroup
                horizon={RangeHorizon.OneWeek}
                activeInsightId={params.activeInsight ?? ''}
                selectedInsights={insightsWithMetadata}
                isError={isInsightsError}
                isInsightsLoading={isInsightsLoading}
                handleSetActiveInsightId={insightId => {
                  openDrawer(insightId, { source: 'asset_overview' });
                }}
              />
            </S.SplitSection>
          </S.Flex>
        )}
      </S.OverviewRoot>
    </>
  );
};
